/**
 * Truth Layer → Zoe Q&A Log
 *
 * Every question asked of Zoe across the organisation, the answer given, the
 * asker's quality feedback (thumbs up / flagged bad) and reviewer notes.
 * Questions that errored instead of being answered are listed too.
 * Backed by GET /api/operations-agent/question-log, which requires an admin
 * or `zoe-question-log` permission — the page shows a permission message
 * rather than an empty list when that's refused.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  CornerDownRight,
  HelpCircle,
  ChevronRight,
  Download,
  Loader2,
  MessagesSquare,
  RefreshCw,
  Search,
  StickyNote,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';
import { addQuestionLogNote, fetchQuestionLog } from '../services/zoeFeedbackApi';
import {
  ANSWER_FEEDBACK_REASON_LABELS,
  type AnswerFeedbackNote,
  type QuestionLogEntry,
  type QuestionLogConversation,
  type QuestionLogErrorKind,
} from '../types/zoeFeedback';
import { downloadTextFile, questionLogToCsv, questionLogToJson } from '../utils/zoeQuestionLogExport';
import { Badge } from '../components/ui/badge';
import { Button } from '@/components/ui/button';
import { billedTokens, formatTokens, groupQuestionLog, type ConversationGroup } from '../utils/zoeQuestionLogGroups';
import TraceDebugPanel from '../components/investigator/TraceDebugPanel';
import { useTab } from '../contexts/TabContext';

type RangeKey = '24h' | '7d' | '30d' | '90d' | 'all';
type FeedbackFilter = 'all' | 'errored' | 'flagged' | 'good' | 'unrated' | 'notes';

const RANGE_OPTIONS: Array<{ key: RangeKey; label: string; hours?: number }> = [
  { key: '24h', label: 'Last 24 hours', hours: 24 },
  { key: '7d', label: 'Last 7 days', hours: 24 * 7 },
  { key: '30d', label: 'Last 30 days', hours: 24 * 30 },
  { key: '90d', label: 'Last 90 days', hours: 24 * 90 },
  { key: 'all', label: 'All time' },
];

const FEEDBACK_FILTERS: Array<{ key: FeedbackFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'errored', label: 'Errored' },
  { key: 'flagged', label: 'Flagged bad' },
  { key: 'good', label: 'Marked good' },
  { key: 'unrated', label: 'No rating' },
  { key: 'notes', label: 'Has notes' },
];

const STATUS_LABELS: Record<QuestionLogEntry['status'], string> = {
  answered: 'Answered',
  needs_clarification: 'Asked for clarification',
  error: 'Errored',
};

const ERROR_KIND_LABELS: Record<QuestionLogErrorKind, string> = {
  investigation_failed: 'Investigation failed',
  narration_failed: 'Answer writing failed',
  narration_disabled: 'Answer writing is switched off',
  answer_contract_violation: 'Answer rejected by the answer contract',
};

/** This page's own tab id — see the re-entry refetch in the component. */
const TAB_ID = 'zoe-question-log';

const DEFAULT_CONVERSATION_LIMIT = 200;
const MAX_CONVERSATION_LIMIT = 1000;

function entryKey(e: QuestionLogEntry): string {
  return `${e.user_id}|${e.conversation_id}|${e.run_id}`;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDuration(ms: number | undefined): ReactNode {
  if (ms === undefined) return <span className="text-muted-foreground">—</span>;
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${Math.round(seconds - minutes * 60)}s`;
}

function matchesFilter(e: QuestionLogEntry, filter: FeedbackFilter): boolean {
  switch (filter) {
    case 'errored':
      return e.status === 'error';
    case 'flagged':
      return e.feedback?.rating === 'down';
    case 'good':
      return e.feedback?.rating === 'up';
    case 'unrated':
      return !e.feedback && e.status !== 'error';
    case 'notes':
      return e.notes.length > 0;
    default:
      return true;
  }
}

function matchesSearch(e: QuestionLogEntry, needle: string): boolean {
  if (!needle) return true;
  const haystack = [
    e.question,
    e.effective_question,
    e.headline,
    e.answer_text,
    e.user_label,
    e.user_id,
    e.feedback?.comment,
    e.error?.message,
    e.error?.detail,
    ...e.notes.map((n) => n.text),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

export default function ZoeQuestionLog() {
  const [range, setRange] = useState<RangeKey>('30d');
  const [feedbackFilter, setFeedbackFilter] = useState<FeedbackFilter>('all');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(DEFAULT_CONVERSATION_LIMIT);
  const [entries, setEntries] = useState<QuestionLogEntry[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [failuresTruncated, setFailuresTruncated] = useState(false);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  // T30 — the log reads as conversations: one open at a time, and within it
  // one question's detail at a time, as the flat list used to.
  const [conversations, setConversations] = useState<QuestionLogConversation[]>([]);
  const [partialConversations, setPartialConversations] = useState(0);
  const [openConversation, setOpenConversation] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const hours = RANGE_OPTIONS.find((o) => o.key === range)?.hours;
      const since = hours ? new Date(Date.now() - hours * 3_600_000).toISOString() : undefined;
      const res = await fetchQuestionLog({ limit, since });
      setEntries(res.entries);
      setConversations(res.conversations ?? []);
      setPartialConversations(res.partial_conversations ?? 0);
      setTruncated(res.truncated);
      setFailuresTruncated(res.failures_truncated ?? false);
      setGeneratedAt(res.generated_at);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not load the question log';
      if (/forbidden|403/i.test(message)) setForbidden(true);
      else setError(message);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [range, limit]);

  useEffect(() => {
    void load();
  }, [load]);

  // App keeps every visited tab mounted and hides it with `display: none`, so
  // coming back to this page does NOT remount it — without this, the list still
  // showed whatever was fetched on first visit and a question asked since
  // appeared only after a manual browser refresh. Refetch on re-entry only; the
  // mount effect above already covers the first visit.
  const { activeTab } = useTab();
  const wasActive = useRef(activeTab === TAB_ID);
  useEffect(() => {
    const isActive = activeTab === TAB_ID;
    if (isActive && !wasActive.current) void load();
    wasActive.current = isActive;
  }, [activeTab, load]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return entries.filter((e) => matchesFilter(e, feedbackFilter) && matchesSearch(e, needle));
  }, [entries, feedbackFilter, search]);

  const filterCounts = useMemo(
    () =>
      Object.fromEntries(
        FEEDBACK_FILTERS.map((f) => [f.key, entries.filter((e) => matchesFilter(e, f.key)).length]),
      ) as Record<FeedbackFilter, number>,
    [entries],
  );
  const userCount = useMemo(() => new Set(entries.map((e) => e.user_id)).size, [entries]);

  // Filters and search work on questions, as before; a conversation is shown
  // when any of its questions match, and keeps its others visible (dimmed) —
  // a flagged answer is judged in the context of the turns around it.
  const matchingKeys = useMemo(() => new Set(filtered.map(entryKey)), [filtered]);
  const groups = useMemo(
    () => groupQuestionLog(entries, conversations).filter((g) => g.turns.some((t) => matchingKeys.has(entryKey(t)))),
    [entries, conversations, matchingKeys],
  );
  const filtering = feedbackFilter !== 'all' || search.trim() !== '';
  const conversationCount = useMemo(
    () => new Set(entries.map((e) => `${e.user_id}|${e.conversation_id}`)).size,
    [entries],
  );
  // What this period cost, from each question's own usage.
  const periodTokens = useMemo(
    () => entries.reduce((n, e) => n + (e.usage ? billedTokens(e.usage) : 0), 0),
    [entries],
  );

  const handleNoteAdded = (key: string, notes: AnswerFeedbackNote[]) =>
    setEntries((prev) => prev.map((e) => (entryKey(e) === key ? { ...e, notes } : e)));

  const exportName = `zoe-question-log_${range}_${new Date().toISOString().slice(0, 10)}`;

  return (
    <div className="space-y-5 w-full max-w-6xl">
      <header>
        <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
          <MessagesSquare className="w-7 h-7 text-chart-10" />
          Zoe Q&amp;A Log
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Questions asked of Zoe across your organisation, the answers given, and how users rated them. Review
          flagged answers and questions that errored, add notes, and download the log.
        </p>
      </header>

      {forbidden ? (
        <div className="rounded-lg border border-warning/40 bg-warning/20 px-4 py-3 text-sm text-warning">
          You don&apos;t have access to the Zoe question log. It shows every user&apos;s questions, so it needs an
          administrator account or the <code className="text-warning">zoe-question-log</code> permission.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card/80 px-3 py-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Period</span>
              <select
                value={range}
                onChange={(e) => setRange(e.target.value as RangeKey)}
                className="rounded-md border border-accent bg-background px-2 py-1.5 text-sm text-foreground"
              >
                {RANGE_OPTIONS.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 flex-1 min-w-48">
              <span className="text-xs text-muted-foreground">Search</span>
              <span className="relative">
                <Search className="w-4 h-4 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2" aria-hidden />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Question, answer, user, comment or note"
                  className="w-full rounded-md border border-accent bg-background pl-8 pr-2 py-1.5 text-sm text-foreground placeholder-muted-foreground"
                />
              </span>
            </label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={() => void load()}
                disabled={loading}
                className="gap-1.5 border-accent hover:border-muted-foreground"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
                Refresh
              </Button>
              <Button
                type="button"
                variant="default"
                size="md"
                disabled={filtered.length === 0}
                onClick={() => downloadTextFile(questionLogToCsv(filtered), `${exportName}.csv`, 'text/csv;charset=utf-8')}
                className="gap-1.5"
              >
                <Download className="w-4 h-4" aria-hidden />
                CSV
              </Button>
              <Button
                type="button"
                variant="outline"
                size="md"
                disabled={filtered.length === 0}
                onClick={() => downloadTextFile(questionLogToJson(filtered), `${exportName}.json`, 'application/json')}
                className="gap-1.5 border-ring text-ring hover:bg-ring/40"
              >
                <Download className="w-4 h-4" aria-hidden />
                JSON
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {FEEDBACK_FILTERS.map((f) => {
              const count = filterCounts[f.key];
              const active = feedbackFilter === f.key;
              return (
                <Button
                  key={f.key}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFeedbackFilter(f.key)}
                  aria-pressed={active}
                  className={[
                    'rounded-full px-3 py-1',
                    active
                      ? 'border-ring/70 bg-ring/15 text-ring'
                      : 'border-accent text-muted-foreground hover:border-muted-foreground',
                  ].join(' ')}
                >
                  {f.label} <span className="text-muted-foreground">({count})</span>
                </Button>
              );
            })}
            <span className="ml-auto text-xs text-muted-foreground">
              {conversationCount} conversation{conversationCount === 1 ? '' : 's'}
              {' · '}
              {entries.length} question{entries.length === 1 ? '' : 's'}
              {' · '}
              {userCount} user{userCount === 1 ? '' : 's'}
              {periodTokens > 0 ? (
                <span title="Tokens billed for every question in this period, counting cached tokens.">
                  {' · '}
                  {formatTokens(periodTokens)} tokens
                </span>
              ) : null}
              {generatedAt ? ` · loaded ${formatWhen(generatedAt)}` : ''}
              {' · downloads include the filtered rows'}
            </span>
          </div>

          {truncated ? (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-warning/40 bg-warning/20 px-3 py-2 text-xs text-warning">
              Showing the {limit} most recently active conversations — older ones aren&apos;t loaded.
              {limit < MAX_CONVERSATION_LIMIT ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setLimit((l) => Math.min(l * 2, MAX_CONVERSATION_LIMIT))}
                  className="px-2 py-0.5 border-warning/60 text-warning hover:bg-warning/40"
                >
                  Load more
                </Button>
              ) : null}
            </div>
          ) : null}

          {failuresTruncated ? (
            <div className="rounded-lg border border-warning/40 bg-warning/20 px-3 py-2 text-xs text-warning">
              More than 1,000 errored questions in this period — only the most recent are shown. Narrow the period to see
              the rest.
            </div>
          ) : null}

          {error ? (
            <div className="rounded-lg border border-destructive/40 bg-destructive/20 px-4 py-3 text-sm text-destructive" role="alert">
              {error}
            </div>
          ) : null}

          {partialConversations > 0 ? (
            <div className="rounded-lg border border-warning/40 bg-warning/20 px-3 py-2 text-xs text-warning">
              {partialConversations} conversation{partialConversations === 1 ? '' : 's'} couldn&apos;t be read in
              full and show only their most recent questions. Refresh to try again.
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-card text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-8 px-2 py-2" aria-label="Expand" />
                  <th className="px-3 py-2 whitespace-nowrap">Last active</th>
                  <th className="px-3 py-2">User</th>
                  <th className="px-3 py-2">Conversation</th>
                  <th
                    className="px-3 py-2 whitespace-nowrap text-right"
                    title="Tokens billed across the whole conversation — every planner, executor, router and narration call, counting cached tokens. Each question's own figure is in its detail."
                  >
                    Usage
                  </th>
                  <th className="px-3 py-2 whitespace-nowrap">Feedback</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-card">
                {loading && entries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                      <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
                      Loading questions…
                    </td>
                  </tr>
                ) : groups.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                      {entries.length === 0 ? 'No questions in this period.' : 'No questions match these filters.'}
                    </td>
                  </tr>
                ) : (
                  groups.map((g) => {
                    const isOpen = openConversation === g.key;
                    const matchCount = g.turns.filter((t) => matchingKeys.has(entryKey(t))).length;
                    const toggle = () => setOpenConversation(isOpen ? null : g.key);
                    return (
                      <Fragment key={g.key}>
                        <tr
                          className={`cursor-pointer align-top hover:bg-card/60 ${isOpen ? 'bg-card/60' : ''}`}
                          onClick={toggle}
                        >
                          <td className="px-2 py-2 text-muted-foreground">
                            <Button
                              type="button"
                              variant="ghost"
                              size="iconSm"
                              aria-expanded={isOpen}
                              aria-label={`${isOpen ? 'Collapse' : 'Expand'} conversation: ${g.title}`}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                toggle();
                              }}
                            >
                              {isOpen ? <ChevronDown className="w-4 h-4" aria-hidden /> : <ChevronRight className="w-4 h-4" aria-hidden />}
                            </Button>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{formatWhen(g.lastAskedAt)}</td>
                          <td className="px-3 py-2 text-muted-foreground max-w-40 truncate" title={g.user_label ?? g.user_id}>
                            {g.user_label ?? `${g.user_id.slice(0, 8)}…`}
                          </td>
                          <td className="px-3 py-2 max-w-112">
                            <span className="line-clamp-2 text-foreground">{g.title}</span>
                            <span className="text-xs text-muted-foreground">
                              {g.turnCount} question{g.turnCount === 1 ? '' : 's'}
                              {filtering ? ` · ${matchCount} match${matchCount === 1 ? 'es' : ''}` : ''}
                            </span>
                          </td>
                          <td
                            className="px-3 py-2 whitespace-nowrap text-right tabular-nums text-muted-foreground"
                            title={
                              g.usage
                                ? `${g.usage.tokens.toLocaleString('en-GB')} tokens · ${(g.usage.durationMs / 1000).toFixed(1)}s waiting across ${g.usage.answers} answer${g.usage.answers === 1 ? '' : 's'}`
                                : undefined
                            }
                          >
                            {g.usage ? formatTokens(g.usage.tokens) : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <ConversationFeedback group={g} />
                          </td>
                        </tr>
                        {isOpen ? (
                          <tr className="bg-background/40">
                            <td colSpan={6} className="px-3 py-3">
                              <ConversationThread
                                group={g}
                                matchingKeys={matchingKeys}
                                expanded={expanded}
                                onToggle={(key) => setExpanded(expanded === key ? null : key)}
                                onNoteAdded={handleNoteAdded}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * One conversation, read in order: each question, what Zoe did with it, and
 * its feedback — a clarification, the choice that answered it, then the
 * answer. Questions that do not match the current filter stay in place,
 * dimmed: they are the context the matching one has to be judged in.
 */
function ConversationThread({
  group,
  matchingKeys,
  expanded,
  onToggle,
  onNoteAdded,
}: {
  group: ConversationGroup;
  matchingKeys: Set<string>;
  expanded: string | null;
  onToggle: (key: string) => void;
  onNoteAdded: (key: string, notes: AnswerFeedbackNote[]) => void;
}) {
  return (
    <ol className="space-y-1 list-none pl-0" aria-label={`Conversation: ${group.title}`}>
      {group.turns.map((t, i) => {
        const key = entryKey(t);
        const isOpen = expanded === key;
        const matches = matchingKeys.has(key);
        return (
          <li key={key} className={matches ? '' : 'opacity-50'}>
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={() => onToggle(key)}
              aria-expanded={isOpen}
              className={`grid w-full h-auto grid-cols-check-2col items-start justify-start gap-x-3 gap-y-1 px-2 py-2 text-left hover:bg-card/60 sm:grid-cols-check-4col ${
                isOpen ? 'bg-card/60' : ''
              }`}
            >
              <span className="pt-0.5 text-xs tabular-nums text-muted-foreground">{t.seq ?? i + 1}</span>
              <span className="min-w-0">
                {t.answered_clarification ? (
                  <span className="flex items-center gap-1 text-xs text-ring/80">
                    <CornerDownRight className="h-3 w-3" aria-hidden />
                    Chose
                  </span>
                ) : null}
                <span className="line-clamp-2 text-foreground">{t.question}</span>
              </span>
              <span className="col-start-2 min-w-0 text-muted-foreground sm:col-start-auto">
                {t.status === 'error' ? (
                  <span className="line-clamp-2 text-destructive">
                    <AlertTriangle className="inline w-3.5 h-3.5 mr-1 -mt-0.5" aria-hidden />
                    Errored: {t.headline}
                  </span>
                ) : t.status === 'needs_clarification' ? (
                  <span className="line-clamp-2 text-warning/90">
                    <HelpCircle className="inline w-3.5 h-3.5 mr-1 -mt-0.5" aria-hidden />
                    Asked which was meant
                  </span>
                ) : (
                  <span className="line-clamp-2">{t.headline}</span>
                )}
              </span>
              <span className="col-start-2 sm:col-start-auto">
                <FeedbackBadge entry={t} />
              </span>
            </Button>
            {isOpen ? (
              <div className="mb-3 mt-2 rounded-lg border border-border bg-background/60 px-4 py-4">
                <EntryDetail entry={t} onNoteAdded={(notes) => onNoteAdded(key, notes)} />
              </div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

/** The conversation's feedback at a glance: how many flagged, good, errored and noted. */
function ConversationFeedback({ group }: { group: ConversationGroup }) {
  if (group.flagged + group.good + group.errored + group.notes === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {group.flagged > 0 ? (
        <Badge tone="destructive" icon={ThumbsDown} title="Answers flagged bad">
          {group.flagged}
        </Badge>
      ) : null}
      {group.good > 0 ? (
        <Badge tone="success" icon={ThumbsUp} title="Answers marked good">
          {group.good}
        </Badge>
      ) : null}
      {group.errored > 0 ? (
        <span className="inline-flex items-center gap-1 text-xs text-destructive" title="Questions that errored">
          <AlertTriangle className="w-3.5 h-3.5" /> {group.errored}
        </span>
      ) : null}
      {group.notes > 0 ? (
        <span className="inline-flex items-center gap-1 text-xs text-ring" title="Reviewer notes">
          <StickyNote className="w-3.5 h-3.5" /> {group.notes}
        </span>
      ) : null}
    </span>
  );
}

function FeedbackBadge({ entry }: { entry: QuestionLogEntry }) {
  return (
    <span className="inline-flex items-center gap-2">
      {entry.feedback?.rating === 'down' ? (
        <Badge tone="destructive" icon={ThumbsDown}>
          Flagged
        </Badge>
      ) : entry.feedback?.rating === 'up' ? (
        <Badge tone="success" icon={ThumbsUp}>
          Good
        </Badge>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      )}
      {entry.notes.length > 0 ? (
        <span className="inline-flex items-center gap-1 text-xs text-ring" title={`${entry.notes.length} note(s)`}>
          <StickyNote className="w-3.5 h-3.5" />
          {entry.notes.length}
        </span>
      ) : null}
    </span>
  );
}

function DetailBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">{label}</p>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}

function EntryDetail({ entry, onNoteAdded }: { entry: QuestionLogEntry; onNoteAdded: (notes: AnswerFeedbackNote[]) => void }) {
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [showTrace, setShowTrace] = useState(false);

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    setNoteError(null);
    try {
      const notes = await addQuestionLogNote({
        userId: entry.user_id,
        conversationId: entry.conversation_id,
        runId: entry.run_id,
        askedAt: entry.asked_at,
        text: noteText.trim(),
      });
      onNoteAdded(notes);
      setNoteText('');
    } catch (err) {
      setNoteError(err instanceof Error ? err.message : 'Could not save note');
    } finally {
      setSaving(false);
    }
  };

  const answerSeen = entry.feedback?.answer_seen;

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="space-y-4">
        <DetailBlock label="Question">
          <p className="whitespace-pre-wrap">{entry.question}</p>
          {entry.effective_question ? (
            <p className="mt-1 text-xs text-muted-foreground">Interpreted as: {entry.effective_question}</p>
          ) : null}
        </DetailBlock>
        {entry.error ? (
          <DetailBlock label="Error">
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 space-y-2">
              <p className="flex items-center gap-2 font-medium text-destructive">
                <AlertTriangle className="w-4 h-4" /> {ERROR_KIND_LABELS[entry.error.kind] ?? entry.error.kind}
              </p>
              <div>
                <p className="text-xs text-muted-foreground">Shown to the user</p>
                <p className="whitespace-pre-wrap">{entry.error.message}</p>
              </div>
              {entry.error.detail && entry.error.detail !== entry.error.message ? (
                <div>
                  <p className="text-xs text-muted-foreground">Technical detail</p>
                  <pre className="whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground">{entry.error.detail}</pre>
                </div>
              ) : null}
              <p className="text-xs text-muted-foreground">
                Failed after {(entry.error.duration_ms / 1000).toFixed(1)}s · {entry.error.endpoint}
                {entry.error.http_status ? ` · HTTP ${entry.error.http_status}` : ''}
              </p>
            </div>
          </DetailBlock>
        ) : (
          <DetailBlock label="Answer">
            {entry.answer_text ? (
              <p className="whitespace-pre-wrap">{entry.answer_text}</p>
            ) : (
              <>
                <p className="whitespace-pre-wrap">{entry.headline}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Only the short headline was stored for this answer (asked before full answers were logged).
                </p>
              </>
            )}
            {answerSeen && answerSeen !== entry.answer_text ? (
              <details className="mt-2 text-xs text-muted-foreground">
                <summary className="cursor-pointer">Answer as shown when rated</summary>
                <p className="mt-1 whitespace-pre-wrap">{answerSeen}</p>
              </details>
            ) : null}
          </DetailBlock>
        )}
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
          <span>
            Status: <span className="text-foreground">{STATUS_LABELS[entry.status]}</span>
          </span>
          {entry.confidence ? (
            <span>
              Confidence: <span className="text-foreground">{entry.confidence}</span>
            </span>
          ) : null}
          {entry.conclusion ? (
            <span>
              Conclusion: <span className="text-foreground">{entry.conclusion}</span>
            </span>
          ) : null}
          {entry.agent_id ? (
            <span>
              Agent: <span className="text-foreground">{entry.agent_id}</span>
            </span>
          ) : null}
          {entry.usage ? (
            <span title="Every billed call in the run — planner, executor, reflector, router and narration — counting prompt, completion and cached tokens.">
              This answer: <span className="text-foreground">{formatTokens(billedTokens(entry.usage))} tokens</span>
              {' · '}
              <span className="text-foreground">{formatDuration(entry.usage.duration_ms)}</span>
            </span>
          ) : null}
        </div>
        {entry.claim_digest.length > 0 ? (
          <DetailBlock label="Evidence digest">
            <ul className="list-disc pl-5 space-y-0.5 text-xs text-muted-foreground">
              {entry.claim_digest.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </DetailBlock>
        ) : null}
        <div className="text-xs text-muted-foreground space-y-0.5">
          <p>Asked by {entry.user_label ?? entry.user_id}</p>
          <p className="font-mono">conversation {entry.conversation_id} · run {entry.run_id}</p>
          {entry.turn_source === 'feedback_snapshot' ? (
            <p className="text-warning/80">
              This turn has aged out of the stored conversation; details come from the copy saved with its feedback.
            </p>
          ) : null}
        </div>
        {/* Failed runs never persist a trace. */}
        {entry.status !== 'error' ? (
          <div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowTrace((s) => !s)}
              className="px-0 text-ring"
            >
              {showTrace ? 'Hide run trace' : 'Show run trace'}
            </Button>
            {showTrace ? (
              <div className="mt-2">
                <TraceDebugPanel runId={entry.run_id} />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="space-y-4">
        {entry.status !== 'error' ? (
          <DetailBlock label="User feedback">
            {entry.feedback ? (
              <div
                className={`rounded-md border px-3 py-2 ${
                  entry.feedback.rating === 'down'
                    ? 'border-destructive/40 bg-destructive/5'
                    : 'border-success/40 bg-success/5'
                }`}
              >
                <p className="flex items-center gap-2 font-medium">
                  {entry.feedback.rating === 'down' ? (
                    <>
                      <ThumbsDown className="w-4 h-4 text-destructive" /> Flagged as a bad answer
                    </>
                  ) : (
                    <>
                      <ThumbsUp className="w-4 h-4 text-success" /> Marked as a good answer
                    </>
                  )}
                </p>
                {entry.feedback.reasons.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {entry.feedback.reasons.map((r) => (
                      <Badge key={r} tone="destructive">
                        {ANSWER_FEEDBACK_REASON_LABELS[r] ?? r}
                      </Badge>
                    ))}
                  </div>
                ) : null}
                {entry.feedback.comment ? (
                  <p className="mt-2 whitespace-pre-wrap text-foreground">&ldquo;{entry.feedback.comment}&rdquo;</p>
                ) : null}
                <p className="mt-2 text-xs text-muted-foreground">{formatWhen(entry.feedback.submitted_at)}</p>
              </div>
            ) : (
              <p className="text-muted-foreground">No rating from the user.</p>
            )}
          </DetailBlock>
        ) : null}

        <DetailBlock label={`Reviewer notes (${entry.notes.length})`}>
          {entry.notes.length > 0 ? (
            <ul className="space-y-2">
              {entry.notes.map((n) => (
                <li key={n.note_id} className="rounded-md border border-border bg-card/60 px-3 py-2">
                  <p className="whitespace-pre-wrap">{n.text}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {n.author_label ?? n.author_id} · {formatWhen(n.created_at)}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground">No notes yet.</p>
          )}
          <div className="mt-3 space-y-2">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value.slice(0, 2000))}
              rows={3}
              placeholder="Add a note — root cause, fix, follow-up…"
              className="w-full rounded-md border border-accent bg-background px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:border-ring focus:outline-none"
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">Notes can&apos;t be edited once added.</span>
              <Button
                type="button"
                variant="default"
                size="sm"
                loading={saving}
                disabled={!noteText.trim()}
                onClick={() => void saveNote()}
                className="gap-1.5"
              >
                <StickyNote className="w-3.5 h-3.5" aria-hidden />
                Add note
              </Button>
            </div>
            {noteError ? <p className="text-xs text-destructive" role="alert">{noteError}</p> : null}
          </div>
        </DetailBlock>
      </div>
    </div>
  );
}
